import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import WidgetUsageKPICard from './components/WidgetUsageKPICard';
import WidgetUsageHeatmap from './components/WidgetUsageHeatmap';
import RoleBasedAccessPatterns from './components/RoleBasedAccessPatterns';
import WidgetPerformanceMatrix from './components/WidgetPerformanceMatrix';
import EngagementTrendChart from './components/EngagementTrendChart';
import AnalyticsFilters from './components/AnalyticsFilters';
import { dashboardCustomizationService } from '../../services/dashboardCustomizationService';
import { userAnalyticsService } from '../../services/userAnalyticsService';
import { widgetCacheService } from '../../services/widgetCacheService';

import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';


const WidgetUsageAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    dateTo: new Date()?.toISOString()?.split('T')?.[0],
    department: '',
    role: ''
  });
  const { companySettings } = useCompanySettings();
  const companyName = companySettings?.companyName || 'HR Management';

  const [kpiMetrics, setKpiMetrics] = useState({
    totalInteractions: 0,
    mostAccessedModule: 'N/A',
    avgSessionDuration: 0,
    engagementScore: 0
  });
  const [widgetUsageData, setWidgetUsageData] = useState([]);
  const [roleAccessPatterns, setRoleAccessPatterns] = useState([]);
  const [widgetPerformanceData, setWidgetPerformanceData] = useState([]);
  const [engagementTrends, setEngagementTrends] = useState([]);
  const [cachePerformance, setCachePerformance] = useState(null);

  const loadCachePerformance = async () => {
    try {
      const metrics = await widgetCacheService?.getCachePerformanceMetrics();
      setCachePerformance(metrics);
    } catch (error) {
      console.error('Error loading cache performance:', error);
    }
  };

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [widgets, pageViews, featureUsage, userPreferences, roleAssignments] = await Promise.all([
        dashboardCustomizationService?.getAllWidgets(),
        userAnalyticsService?.getPageViews({ dateFrom: filters?.dateFrom, dateTo: filters?.dateTo }),
        userAnalyticsService?.getFeatureUsage({ dateFrom: filters?.dateFrom, dateTo: filters?.dateTo }),
        supabase?.from('user_widget_preferences')?.select('*'),
        supabase?.from('user_role_assignments')?.select('*, custom_roles(role_name)')
      ]);

      // Calculate KPI metrics
      const totalInteractions = (pageViews?.length || 0) + (featureUsage?.length || 0);
      
      // Find most accessed module
      const moduleAccess = {};
      pageViews?.forEach(view => {
        const module = view?.page_path?.split('/')?.[1] || 'unknown';
        moduleAccess[module] = (moduleAccess?.[module] || 0) + 1;
      });
      const mostAccessedModule = Object.entries(moduleAccess)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] || 'N/A';

      // Calculate average session duration
      const sessions = await userAnalyticsService?.getUserSessions({ dateFrom: filters?.dateFrom, dateTo: filters?.dateTo });
      const avgSessionDuration = sessions?.length > 0
        ? (sessions?.reduce((sum, s) => sum + (s?.durationMinutes || 0), 0) / sessions?.length)?.toFixed(1)
        : 0;

      // Calculate engagement score (interactions per user per day)
      const uniqueUsers = new Set(pageViews?.map(v => v?.user_email));
      const daysDiff = Math.max(1, Math.ceil((new Date(filters?.dateTo) - new Date(filters?.dateFrom)) / (1000 * 60 * 60 * 24)));
      const engagementScore = uniqueUsers?.size > 0
        ? ((totalInteractions / uniqueUsers?.size) / daysDiff)?.toFixed(1)
        : 0;

      setKpiMetrics({
        totalInteractions,
        mostAccessedModule: mostAccessedModule?.replace(/-/g, ' ')?.toUpperCase(),
        avgSessionDuration: parseFloat(avgSessionDuration),
        engagementScore: parseFloat(engagementScore)
      });

      // Process widget usage data
      const widgetUsage = widgets?.map(widget => {
        const widgetViews = pageViews?.filter(v => v?.page_path?.includes(widget?.widgetName?.toLowerCase()?.replace(/\s+/g, '-')))?.length || 0;
        const widgetPrefs = userPreferences?.data?.filter(p => p?.widget_id === widget?.id) || [];
        const visibleCount = widgetPrefs?.filter(p => p?.is_visible)?.length || 0;
        const totalUsers = uniqueUsers?.size || 1;
        const adoptionRate = ((visibleCount / totalUsers) * 100)?.toFixed(1);

        return {
          widgetName: widget?.widgetName,
          widgetType: widget?.widgetType,
          interactions: widgetViews,
          adoptionRate: parseFloat(adoptionRate),
          avgTimeSpent: Math.floor(Math.random() * 180) + 30 // Simulated for now
        };
      })?.sort((a, b) => b?.interactions - a?.interactions);

      setWidgetUsageData(widgetUsage);

      // Process role-based access patterns
      const rolePatterns = {};
      roleAssignments?.data?.forEach(assignment => {
        const roleName = assignment?.custom_roles?.role_name || 'Unknown';
        if (!rolePatterns?.[roleName]) {
          rolePatterns[roleName] = {
            roleName,
            totalAccess: 0,
            topModules: {}
          };
        }
        
        const userViews = pageViews?.filter(v => v?.user_email === assignment?.user_email) || [];
        rolePatterns[roleName].totalAccess += userViews?.length;
        
        userViews?.forEach(view => {
          const module = view?.page_path?.split('/')?.[1] || 'unknown';
          rolePatterns[roleName].topModules[module] = (rolePatterns?.[roleName]?.topModules?.[module] || 0) + 1;
        });
      });

      const roleAccessData = Object.values(rolePatterns)?.map(pattern => ({
        roleName: pattern?.roleName,
        totalAccess: pattern?.totalAccess,
        topModule: Object.entries(pattern?.topModules)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] || 'N/A',
        moduleCount: Object.keys(pattern?.topModules)?.length
      }))?.sort((a, b) => b?.totalAccess - a?.totalAccess);

      setRoleAccessPatterns(roleAccessData);

      // Process widget performance matrix
      const performanceData = widgetUsage?.map(widget => ({
        widgetName: widget?.widgetName,
        loadTime: Math.floor(Math.random() * 2000) + 500, // Simulated
        errorRate: (Math.random() * 5)?.toFixed(2), // Simulated
        satisfactionScore: (Math.random() * 30 + 70)?.toFixed(1), // Simulated
        clickThroughRate: widget?.adoptionRate
      }));

      setWidgetPerformanceData(performanceData);

      // Generate engagement trends (last 14 days)
      const trendData = [];
      for (let i = 13; i >= 0; i--) {
        const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
        const dateStr = date?.toISOString()?.split('T')?.[0];
        const dayViews = pageViews?.filter(v => v?.viewed_at?.startsWith(dateStr))?.length || 0;
        const dayFeatures = featureUsage?.filter(f => f?.usedAt?.startsWith(dateStr))?.length || 0;
        
        trendData?.push({
          date: date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          widgetViews: dayViews,
          interactions: dayFeatures,
          engagement: dayViews + dayFeatures
        });
      }

      setEngagementTrends(trendData);

    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalyticsData();
    loadCachePerformance();
    
    const interval = setInterval(loadCachePerformance, 30000);
    return () => clearInterval(interval);
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleExportReport = () => {
    const reportData = {
      generatedAt: new Date()?.toISOString(),
      period: `${filters?.dateFrom} to ${filters?.dateTo}`,
      kpiMetrics,
      widgetUsageData,
      roleAccessPatterns,
      widgetPerformanceData,
      engagementTrends
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `widget-usage-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <ComplianceAlertBanner />
        <LegalDisclaimerBanner />
        <GlobalFilterBar onFilterChange={handleFilterChange} />
        <DataRefreshIndicator />
        <main className="pt-32 pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading widget analytics...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <ComplianceAlertBanner />
        <LegalDisclaimerBanner />
        <GlobalFilterBar onFilterChange={handleFilterChange} />
        <DataRefreshIndicator />
        <main className="pt-32 pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <p className="text-blue-800 font-medium mb-2">Error Loading Analytics</p>
              <p className="text-muted-foreground text-sm">{error}</p>
              <button
                onClick={loadAnalyticsData}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth"
              >
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Widget Usage Analytics Dashboard - {companyName}</title>
        <meta name="description" content="Comprehensive insights into dashboard widget performance, user engagement patterns, and optimization opportunities" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <ComplianceAlertBanner />
        <LegalDisclaimerBanner />
        <GlobalFilterBar onFilterChange={handleFilterChange} />
        <DataRefreshIndicator />

        <main className="pt-32 pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-3">
                    Widget Usage Analytics Dashboard
                  </h1>
                  <p className="text-base md:text-lg text-muted-foreground max-measure">
                    Track widget performance, engagement patterns, and optimization opportunities
                  </p>
                </div>
                <button
                  onClick={handleExportReport}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth flex items-center gap-2"
                >
                  <span>Export Report</span>
                </button>
              </div>
            </div>

            <AnalyticsFilters filters={filters} onChange={handleFilterChange} />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
              <WidgetUsageKPICard
                title="Total Widget Interactions"
                value={kpiMetrics?.totalInteractions?.toLocaleString()}
                subtitle="across all widgets"
                icon="MousePointer"
                trend={15.3}
                status="compliant"
              />
              <WidgetUsageKPICard
                title="Most Accessed Module"
                value={kpiMetrics?.mostAccessedModule}
                subtitle="by all users"
                icon="TrendingUp"
                trend={0}
                status="compliant"
              />
              <WidgetUsageKPICard
                title="Avg Session Duration"
                value={`${kpiMetrics?.avgSessionDuration}m`}
                subtitle="per user session"
                icon="Clock"
                trend={-3.2}
                status="warning"
              />
              <WidgetUsageKPICard
                title="Engagement Score"
                value={kpiMetrics?.engagementScore}
                subtitle="interactions/user/day"
                icon="Activity"
                trend={22.7}
                status="compliant"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
              <div className="lg:col-span-8">
                <EngagementTrendChart data={engagementTrends} />
              </div>
              <div className="lg:col-span-4">
                <WidgetUsageHeatmap data={widgetUsageData} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <RoleBasedAccessPatterns data={roleAccessPatterns} />
              <WidgetPerformanceMatrix data={widgetPerformanceData} />
            </div>

            {/* Cache Performance Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Cache Performance</h2>
                  <p className="text-sm text-gray-600 mt-1">Real-time widget cache efficiency metrics</p>
                </div>
                <button
                  onClick={loadCachePerformance}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Icon name="refresh-cw" className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {cachePerformance && (
                <div className="space-y-6">
                  {/* Cache Statistics Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">Hit Rate</p>
                          <p className="text-2xl font-bold text-green-900 mt-1">{cachePerformance?.hitRate}%</p>
                        </div>
                        <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                          <Icon name="zap" className="w-6 h-6 text-green-700" />
                        </div>
                      </div>
                      <p className="text-xs text-green-700 mt-2">
                        {cachePerformance?.hits} hits / {cachePerformance?.totalRequests} requests
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-800">Cache Size</p>
                          <p className="text-2xl font-bold text-blue-900 mt-1">{cachePerformance?.currentSize}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                          <Icon name="database" className="w-6 h-6 text-blue-700" />
                        </div>
                      </div>
                      <p className="text-xs text-blue-700 mt-2">
                        {cachePerformance?.memoryUsageMB} MB / {cachePerformance?.maxSize} max entries
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-purple-800">Avg Access</p>
                          <p className="text-2xl font-bold text-purple-900 mt-1">{cachePerformance?.avgAccessCount}</p>
                        </div>
                        <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                          <Icon name="trending-up" className="w-6 h-6 text-purple-700" />
                        </div>
                      </div>
                      <p className="text-xs text-purple-700 mt-2">
                        Frequently accessed: {cachePerformance?.frequentlyAccessedCount}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-orange-800">Evictions</p>
                          <p className="text-2xl font-bold text-orange-900 mt-1">{cachePerformance?.evictions}</p>
                        </div>
                        <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center">
                          <Icon name="trash-2" className="w-6 h-6 text-orange-700" />
                        </div>
                      </div>
                      <p className="text-xs text-orange-700 mt-2">
                        Expired: {cachePerformance?.expiredEntries}
                      </p>
                    </div>
                  </div>

                  {/* Cache Efficiency Indicator */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Cache Efficiency</span>
                      <span className="text-sm font-semibold text-gray-900">{cachePerformance?.cacheEfficiency}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${
                          cachePerformance?.cacheEfficiency >= 80
                            ? 'bg-green-500'
                            : cachePerformance?.cacheEfficiency >= 60
                            ? 'bg-yellow-500' :'bg-red-500'
                        }`}
                        style={{ width: `${cachePerformance?.cacheEfficiency}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-600">
                        Avg TTL Remaining: {Math.round(cachePerformance?.avgTTLRemainingMs / 1000)}s
                      </span>
                      <span className="text-xs text-gray-600">
                        {cachePerformance?.cacheEfficiency >= 80
                          ? 'Excellent'
                          : cachePerformance?.cacheEfficiency >= 60
                          ? 'Good' :'Needs Optimization'}
                      </span>
                    </div>
                  </div>

                  {/* Cache Configuration */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Cache Configuration</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Default TTL</p>
                        <p className="font-semibold text-gray-900">{cachePerformance?.config?.DEFAULT_TTL / 1000}s</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Frequent TTL</p>
                        <p className="font-semibold text-gray-900">{cachePerformance?.config?.FREQUENT_ACCESS_TTL / 1000}s</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Max Size</p>
                        <p className="font-semibold text-gray-900">{cachePerformance?.config?.MAX_CACHE_SIZE}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Auto Warm</p>
                        <p className="font-semibold text-gray-900">{cachePerformance?.config?.AUTO_WARM_INTERVAL / 1000}s</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!cachePerformance && (
                <div className="text-center py-8 text-gray-500">
                  <Icon name="loader" className="w-8 h-8 mx-auto mb-2 animate-spin" />
                  <p>Loading cache performance metrics...</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </React.Fragment>
  );
};

export default WidgetUsageAnalyticsDashboard;